import React, { useEffect, useState } from "react";
import styles from "../modals/ResourceViewerModal.module.css";
import cardStyles from "./CareTeamCardExtended.module.css";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultProvider from "../assets/Doctor_Male_96.png";
import DefaultOrg from "../assets/world-health.png";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchEncounterByReference } from "../services/encounterService";
import { fetchCareTeamByReference } from "../services/careTeamService";
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";
import { formatDate } from "../utils/dateUtils";
import { useStore } from "react-redux";

const Field = ({ label, children }) => (
    <div className="mb-1">
        <strong>{label}:</strong> {children ?? <span className="text-muted">N/A</span>}
    </div>
);

const joinArray = (arr, fn = (x) => x) => {
    if (!arr || !arr.length) return null;
    return arr.map(fn).filter(Boolean).join(", ");
};

const renderRefs = (arr) => joinArray(arr, r => r.display || r.reference);
const renderIdentifiers = (ids) => joinArray(ids, id => id.system ? `${id.system}|${id.value}` : id.value || id.use || null) || "None";
const renderCoding = (cats) => {
    if (!cats) return null;
    return joinArray(cats, c => c.coding ? joinArray(c.coding, cc => cc.display || cc.code) : c.text || null);
};


const CarePlanCardExtended = ({ resource, handleShowNested, fhirClient, }) => {
    const store = useStore();
    const status = resource.status || "Unknown";
    const intent = resource.intent || "Unknown";
    const period = resource.period || {};
    const addresses = renderRefs(resource.addresses) || "None";
    const categories = renderCoding(resource.category) || "None";
    const identifiers = renderIdentifiers(resource.identifier) || "None";
    const instantiates = joinArray(resource.instantiatesCanonical || resource.instantiatesUri || [], i => i);
    const basedOn = renderRefs(resource.basedOn);
    const replaces = renderRefs(resource.replaces);
    const partOf = renderRefs(resource.partOf);
    const encounter = resource.encounter?.display || resource.encounter?.reference;
    const created = resource.created;
    const contributors = renderRefs(resource.contributor) || "None";
    const careTeams = resource.careTeam || null;
    const supportingInfo = renderRefs(resource.supportingInfo) || "None";
    const goals = resource.goal || null;
    const notes = joinArray(resource.note, n => n.text) || "None";
    const language = resource.language || "unknown";
    const statusReason = resource.statusReason ? (resource.statusReason.text || joinArray(resource.statusReason.coding, cc => cc.display || cc.code)) : null;
    const activities = resource.activity?.length
        ? resource.activity.map((act, idx) => {
            const detail = act.detail || {};
            return (
                <div key={idx} className="mb-2">
                    <strong>{detail.code?.text || detail.code?.coding?.[0]?.display || "Activity"}</strong>
                    {detail.status && <> — <span className="text-muted">{detail.status}</span></>}
                    {detail.description && <div className="ps-3">{detail.description}</div>}
                </div>
            );
        })
        : [<div key="none">None listed</div>];


    const [subject, setSubject] = useState(null);
    const [subjectName, setSubjectName] = useState(null);
    const [encounterName, setEncounterName] = useState(null);
    const [encounterData, setEncounterData] = useState(null);
    const [performers, setPerformers] = useState([]);
    const [provider, setProvider] = useState(null);
    const [interpreter, setInterpreter] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [author, setAuthor] = useState(null);


    useEffect(() => {
        if (!resource || !fhirClient) return;

        hydrateFhirGraph(resource, fhirClient, store);

        const doFetchResources = async () => {
            // ---------- SUBJECT ----------
            if (resource.subject?.reference) {
                const patient = await fetchPatientByReference(
                    resource.subject.reference,
                    fhirClient,
                    store
                );
                setSubject(patient);
                setSubjectName(resolveName(patient));
            }

            // ---------- ENCOUNTER ----------
            if (resource.encounter?.reference) {
                const encounter = await fetchEncounterByReference(
                    resource.encounter.reference,
                    fhirClient,
                    store
                );
                setEncounterData(encounter);
                setEncounterName(resolveName(encounter));
            }

            // ---------- AUTHOR ----------
            // -- Get Author
            if (resource.author?.reference) {
                const ref = resource.author.reference;
                const [type] = ref.split("/");

                let resolved = null;

                switch (type) {
                    case "Practitioner":
                        resolved = await fetchProviderByReference(ref, fhirClient, store);
                        // show practitioner as interpreter/author
                        setInterpreter(resolved);
                        setProvider(resolved);
                        break;

                    case "Organization":
                        resolved = await fetchOrganizationByReference(ref, fhirClient, store);
                        setOrganization(resolved);
                        break;

                    case "Patient":
                        resolved = await fetchPatientByReference(ref, fhirClient, store);
                        setInterpreter(resolved);
                        setSubject(resolved);
                        setSubjectName(resolveName(resolved));
                        break;

                    case "CareTeam":
                        resolved = await fetchCareTeamByReference(ref, fhirClient, store);
                        setInterpreter(resolved);
                        break;

                    default:
                        // Fallback: attempt generic fetch for less-common types (Device, RelatedPerson, PractitionerRole, etc.)
                        try {
                            resolved = await fhirClient.request(ref);
                        } catch (err) {
                            console.warn("Failed to fetch author reference:", ref, err);
                        }
                        // prefer setting interpreter for display
                        if (resolved) setInterpreter(resolved);
                }

                setAuthor(resolved || resource.author);
            }
        }
        doFetchResources();


    }, [resource, fhirClient, store]);


    /* -------------------- Render -------------------- */
    const patientPhotoUrl = subject?.photo?.[0]?.url || (subject?.gender === "female" ? FemalePatient : MalePatient);
    const providerFullName = resolveName(provider);
    const interpreterFullName = resolveName(interpreter);
    const providerPhotoUrl = provider?.photo?.[0]?.url || (provider?.gender === "female" ? FemaleProvider : MaleProvider);
    const interpreterPhotoUrl = interpreter?.photo?.[0]?.url || (interpreter?.gender === "female" ? FemaleProvider : MaleProvider);
    const orgDisplayName = organization?.name || (organization?.id ? `Organization/${organization.id}` : "Unknown Organization");
    const orgIcon = DefaultOrg || DefaultOrg || ""; // replace DefaultProvider with your org icon path if available


    if (!resource) return <div>No CarePlan data available.</div>;

    return (
        <div className="p-4 border rounded shadow-sm bg-white">

            {/* ===================== SUBJECT + PERFORMER (Patient, Provider, Org) ===================== */}
            <div className={cardStyles.headerRow}>
                <div className={cardStyles.entityBlock}>
                    <button
                        className={cardStyles.entityImageButton}
                        onClick={() => handleShowNested?.(subject, subjectName)}
                    >
                        <img src={patientPhotoUrl} alt="Patient" className={cardStyles.avatar} />
                    </button>
                    <div>
                        <Field label="Patient">{subjectName}</Field>
                        <Field label="Birth Date">{subject?.birthDate}</Field>
                    </div>
                </div>

                {/* Provider + Organization block */}
                <div className={cardStyles.organizationBlock}>
                    <button
                        className={cardStyles.entityImageButton}
                        onClick={() => handleShowNested?.(interpreter, providerFullName)}
                    >
                        <img src={interpreterPhotoUrl || DefaultProvider} alt="Interpreter" className={cardStyles.avatar} />
                    </button>

                    <div className={cardStyles.orgText} style={{ marginRight: "1rem" }}>
                        <Field label="Author">{interpreterFullName}</Field>
                        <Field label="Active">{interpreter ? (interpreter.active ? "Yes" : "No") : "N/A"}</Field>
                    </div>


                </div>
            </div>


            {/* Header */}
            <div className={styles.sectionHeader}>Identifiers</div>
            <div className="mb-3">
                <div className="row">
                    <div className="col-md-6 mb-1 text-muted">
                        <strong>CarePlan ID:</strong> {resource.id}
                    </div>

                    <div className="col-md-6 mb-1 text-muted">
                        <strong>Identifiers:</strong> {identifiers}
                    </div>

                    <div className="col-md-6 mb-1 text-muted">
                        <strong>Status:</strong> {status}
                    </div>

                    <div className="col-md-6 mb-1 text-muted">
                        <strong>Intent:</strong> {intent}
                    </div>

                    <div className="col-md-6 mb-1 text-muted">
                        <strong>Language:</strong> {language}
                    </div>

                    {instantiates && (
                        <div className="col-md-6 mb-1 text-muted">
                            <strong>Instantiates:</strong> {instantiates}
                        </div>
                    )}

                    {basedOn && (
                        <div className="col-md-6 mb-1 text-muted">
                            <strong>Based On:</strong> {basedOn}
                        </div>
                    )}

                    {replaces && (
                        <div className="col-md-6 mb-1 text-muted">
                            <strong>Replaces:</strong> {replaces}
                        </div>
                    )}

                    {partOf && (
                        <div className="col-md-6 mb-1 text-muted">
                            <strong>Part Of:</strong> {partOf}
                        </div>
                    )}

                    <div className="col-md-6 mb-1 text-muted">
                        <strong>Period:</strong> {formatDate(period.start)} – {formatDate(period.end)}
                    </div>
                </div>
            </div>



            {/* Context */}
            <div className={styles.sectionHeader}>Context</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">
                        <strong>Encounter:</strong>{" "}
                        {resource.encounter?.reference ? (renderReference(resource.encounter.reference, resolveName(resource.encounter.reference, store), handleShowNested))
                            : (
                                <span className="text-muted">None</span>
                            )}
                    </div>
                    <div className="mb-1"><strong>Created:</strong> {created ? formatDate(created) : "Unknown"}</div>
                    {statusReason && <div className="mb-1"><strong>Status Reason:</strong> {statusReason}</div>}
                </div>
            </div>

            {/* Categories */}
            <div className={styles.sectionHeader}>Categories</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">{categories}</div>
                </div></div>

            {/* Addresses */}
            <div className={styles.sectionHeader}>Addresses</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">
                        {resource.addresses && resource.addresses.length ? (
                            resource.addresses.map((a, idx) => {
                                const ref = a.reference || a;
                                const label = a.display || resolveName(ref, store) || (typeof a === 'string' ? a : a.reference);
                                return (
                                    <span key={a.reference || idx}>
                                        {renderReference(ref, label, handleShowNested)}{idx < resource.addresses.length - 1 ? ', ' : ''}
                                    </span>
                                );
                            })
                        ) : (
                            <span className="text-muted">None</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Care Team & Contributors */}
            <div className={styles.sectionHeader}>Care Team & Contributors</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1"><strong>Care Teams:</strong>{' '}
                        {careTeams && careTeams.length ? (
                            careTeams.map((ct, idx) => (
                                <span key={ct.reference || idx}>
                                    {renderReference(ct.reference || ct, ct.display || ct.reference || ct, handleShowNested)}{idx < careTeams.length - 1 ? ', ' : ''}
                                </span>
                            ))
                        ) : (
                            <span className="text-muted">None</span>
                        )}
                    </div>
                    <div className="mb-1"><strong>Contributors:</strong> {contributors}</div>
                </div></div>

            {/* Supporting Info */}
            <div className={styles.sectionHeader}>Supporting Info</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">{supportingInfo}</div>
                </div></div>

            {/* Goals */}
            <div className={styles.sectionHeader}>Goals</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">
                        {goals && goals.length ? (
                            goals.map((g, idx) => (
                                <span key={g.reference || idx}>
                                    {renderReference(g.reference || g, g.display || g.reference || g, handleShowNested)}{idx < goals.length - 1 ? ', ' : ''}
                                </span>
                            ))
                        ) : (
                            <span className="text-muted">None</span>
                        )}
                    </div>
                </div></div>

            {/* Notes */}
            <div className={styles.sectionHeader}>Notes</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1">{notes}</div>
                </div></div>

            {/* Activities */}
            <div className={styles.sectionHeader}>Activities</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    {activities}
                </div></div>

            {/* Description */}
            {resource.description && (
                <>
                    <div className={styles.sectionHeader}>Description</div>
                    <div className={styles.indented}>
                        <div className="mb-1">{resource.description}</div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CarePlanCardExtended;